import * as Popover from "@radix-ui/react-popover";
import { useState } from "react";

export interface KebabAction {
  label: string;
  /** "default" / "warning" / "danger" — picks the foreground color token. */
  tone?: "default" | "warning" | "danger";
  /** Click handler. The menu closes synchronously before the handler runs so
   *  a follow-up dialog can claim focus without competing with the popover. */
  onSelect: () => void;
  disabled?: boolean;
  testid?: string;
}

interface KebabMenuProps {
  testid?: string;
  ariaLabel?: string;
  actions: KebabAction[];
}

const toneClass: Record<NonNullable<KebabAction["tone"]>, string> = {
  default: "text-fg-soft hover:bg-bg/40",
  warning: "text-warning hover:bg-warning-soft",
  danger: "text-danger hover:bg-danger-soft",
};

/**
 * Vertical-ellipsis (⋯) action menu for row-level admin operations. Keeps the
 * row line compact when there are 2-3 actions per row. Items can either be
 * plain buttons (use `onSelect`) or custom triggers like ConfirmDialog (use
 * `render`).
 */
export function KebabMenu({ testid, ariaLabel = "Aktionen", actions }: KebabMenuProps) {
  const [open, setOpen] = useState(false);

  if (actions.length === 0) {
    return <span className="text-fg-faint text-xs">—</span>;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          data-testid={testid}
          className="min-h-[36px] min-w-[36px] rounded-lg text-fg-muted hover:text-fg hover:bg-bg/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong inline-flex items-center justify-center"
        >
          <span aria-hidden className="text-lg leading-none">⋯</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[180px] bg-elevated border border-outline rounded-lg shadow-modal py-1 animate-fade-in"
        >
          {actions.map((a, i) => {
            const tone = toneClass[a.tone ?? "default"];
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={a.disabled}
                data-testid={a.testid}
                onClick={() => {
                  setOpen(false);
                  // Defer the action until after Radix unmounts the popover —
                  // a follow-up dialog needs the focus stack clear of the
                  // popover's content node.
                  queueMicrotask(() => a.onSelect());
                }}
                className={`w-full px-3 py-2 text-left text-sm font-medium ${tone} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {a.label}
              </button>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
